import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { AlternateStudent } from './alternate-student';
export { AlternateStudent };
import { alternateStudentSchema } from '@schemas/business-objects/alternate-student';
import { AlternateStudentAvailabilityFormArray } from './alternate-student-availability-form-group';
import { NotAvailableFormArray } from './not-available-form-group';
import { StudentFormGroup } from './student-form-group';
import { TermFormGroup } from './term-form-group';
import type { CalendarDate } from '../base-types';
import type { Organization } from './organization';
import type { User } from './user';

export class AlternateStudentFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    created: SchemaFormControl;
    inactiveEffectiveDate: SchemaFormControl;
    statusNotes: SchemaFormControl;
    termId: SchemaFormControl;
    term: TermFormGroup;
    studentId: SchemaFormControl;
    student: StudentFormGroup;
    notAvailables: NotAvailableFormArray;
    availabilities: AlternateStudentAvailabilityFormArray;
  };

  declare value: Partial<AlternateStudent>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(alternateStudentSchema, AlternateStudent, data, options);
  }

  get object(): AlternateStudent {
    return new AlternateStudent(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'term':
        return new TermFormGroup();
      case 'student':
        return new StudentFormGroup();
      case 'notAvailables':
        return new NotAvailableFormArray();
      case 'availabilities':
        return new AlternateStudentAvailabilityFormArray();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<AlternateStudent[]> {
    return AlternateStudent.find(filter);
  }

  static async findOne(filter?: any): Promise<AlternateStudent | null> {
    return AlternateStudent.findOne(filter);
  }

  static async findById(id: number, filter?: any): Promise<AlternateStudent> {
    return AlternateStudent.findById(id, filter);
  }

  static async create(
    data: Partial<AlternateStudent>,
  ): Promise<AlternateStudent> {
    return AlternateStudent.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<AlternateStudent>,
  ): Promise<AlternateStudent> {
    return AlternateStudent.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return AlternateStudent.deleteById(id);
  }

  async markInactive(
    effectiveDate?: CalendarDate | null,
  ): Promise<AlternateStudentFormGroup> {
    const instance = new AlternateStudent(this.getRawValue());
    const result = await instance.markInactive(effectiveDate);
    const fg = new AlternateStudentFormGroup();
    fg._populate(result);
    return fg;
  }
}

export class AlternateStudentFormArray extends PersistedFormArray {
  readonly entityName = 'AlternateStudent' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(alternateStudentSchema, AlternateStudent, options);
  }

  protected override createItemGroup() {
    return new AlternateStudentFormGroup();
  }

  get array(): AlternateStudent[] {
    return this.controls.map(
      (group: AlternateStudentFormGroup) => group.object,
    );
  }
}

export class AlternateStudentPersistedArray extends PersistedArray<AlternateStudent> {
  readonly entityName = 'AlternateStudent' as const;

  constructor(options?: PersistedArrayOptions) {
    super(AlternateStudent, options);
  }
}
